import React from "react";
import { Box, Typography, Stack, useTheme, Tooltip } from "@mui/material";
import { formatChartTitle, getMetricTarget, meetsTarget } from "../../utils";

interface MetricTargetBarsData {
  [key: string]: string | number;
}

interface MetricTargetBarsProps {
  /** One row per metric. Must contain the metric label under `metricKey`. */
  data: MetricTargetBarsData[];
  /** Keys of the value series to plot per metric (e.g. ["currentScore"] or ["group_0", "group_1"]). */
  seriesKeys: string[];
  /** Display labels for each series, index-aligned with seriesKeys. */
  seriesLabels: string[];
  /** Key holding the metric label in each row. */
  metricKey?: string;
  title?: string;
  height?: number;
  /** Per-series colours, index-aligned with seriesKeys. */
  colors?: string[];
}

/**
 * "Value vs target" bars for the Organisation Health metrics. Replaces the old
 * radar that plotted mixed-unit metrics (%, bpm, ms, 0–21) on a single 0–100
 * axis against a flat target of 100 (BT-110 / BT-224). Each metric is scaled
 * against its own target, with a target marker and unit, and lower-is-better
 * metrics (Resting HR, Daily Strain) are handled correctly.
 */
export const MetricTargetBars: React.FC<MetricTargetBarsProps> = ({
  data,
  seriesKeys,
  seriesLabels,
  metricKey = "metric",
  title,
  height,
  colors,
}) => {
  const theme = useTheme();
  const formattedTitle = formatChartTitle(title);
  const isSingleSeries = seriesKeys.length === 1;

  const defaultColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
  ];
  const seriesColor = (index: number) =>
    colors?.[index] || defaultColors[index % defaultColors.length];

  return (
    <Box sx={{ width: "100%", minHeight: height }}>
      {formattedTitle && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}

      {/* Legend (only meaningful for multi-series comparison) */}
      {!isSingleSeries && (
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
          {seriesLabels.map((label, i) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: seriesColor(i) }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Stack spacing={2}>
        {data.map((row) => {
          const label = String(row[metricKey]);
          const target = getMetricTarget(label);
          const values = seriesKeys.map((k) => Number(row[k]) || 0);

          // Domain: stretch to cover the target and all values, with headroom.
          const domainMax =
            Math.max(target?.target ?? 0, ...values, 1) * 1.2;
          const unit = target?.unit ?? "";
          const targetPct = target ? (target.target / domainMax) * 100 : null;

          return (
            <Box key={label}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
                {target && (
                  <Typography variant="caption" color="text.secondary">
                    Target {target.direction === "lower" ? "≤" : "≥"} {target.target}
                    {unit}
                  </Typography>
                )}
              </Box>

              {/* Track with bars + target marker */}
              <Box sx={{ position: "relative" }}>
                <Stack spacing={0.5}>
                  {seriesKeys.map((key, i) => {
                    const value = Number(row[key]) || 0;
                    const widthPct = Math.min((value / domainMax) * 100, 100);
                    const ok = isSingleSeries
                      ? meetsTarget(label, value)
                      : undefined;
                    const barColor =
                      ok === undefined
                        ? seriesColor(i)
                        : ok
                          ? theme.palette.success.main
                          : theme.palette.error.main;
                    return (
                      <Tooltip
                        key={key}
                        arrow
                        title={`${seriesLabels[i]}: ${Math.round(value * 10) / 10}${unit}${
                          target ? ` (target ${target.direction === "lower" ? "≤" : "≥"} ${target.target}${unit})` : ""
                        }`}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            height: 18,
                            borderRadius: 1,
                            backgroundColor: theme.palette.action.hover,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${widthPct}%`,
                              backgroundColor: barColor,
                              borderRadius: 1,
                              transition: "width 0.3s ease",
                            }}
                          />
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>

                {/* Target marker line across the track */}
                {targetPct !== null && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -2,
                      bottom: -2,
                      left: `${Math.min(targetPct, 100)}%`,
                      width: "2px",
                      backgroundColor: theme.palette.text.primary,
                      opacity: 0.6,
                    }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
